import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Button,
    Divider,
    Grid,
    Skeleton,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';
import { useTranslation } from '../../i18n';
import cartService from '../../services/cartService';

const CartPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { cart, totals, loading, error, removeItem, clearCart } = useCart();

    const handleRemoveItem = async (itemId: number) => {
        await removeItem(itemId);
    };

    const handleClearCart = async () => {
        await clearCart();
    };

    const handleContinueShopping = () => {
        navigate('/library');
    };

    const handleCheckout = () => {
        navigate('/checkout');
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCartIcon color="primary" />
                    {t('cart.title')}
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Card>
                            <CardContent>
                                {[1, 2, 3].map((i) => (
                                    <Box key={i} sx={{ mb: 2 }}>
                                        <Skeleton variant="rectangular" height={80} />
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card>
                            <CardContent>
                                <Skeleton variant="text" height={40} />
                                <Skeleton variant="text" />
                                <Skeleton variant="text" />
                                <Skeleton variant="rectangular" height={50} sx={{ mt: 2 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const isEmpty = !cart || cart.items.length === 0;

    if (isEmpty) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCartIcon color="primary" />
                    {t('cart.title')}
                </Typography>
                <Card sx={{ mt: 3, textAlign: 'center', py: 6 }}>
                    <CardContent>
                        <ShoppingCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {t('cart.empty.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('cart.empty.description')}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleContinueShopping}
                        >
                            {t('cart.actions.continueShopping')}
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon color="primary" />
                {t('cart.title')}
            </Typography>

            <Grid container spacing={3}>
                {/* Cart Items */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Produkt</TableCell>
                                    <TableCell align="right">Preis</TableCell>
                                    <TableCell align="center">Menge</TableCell>
                                    <TableCell align="right">Summe</TableCell>
                                    <TableCell align="center" width={60}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cart.items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    variant="rounded"
                                                    src={item.product?.thumbnailUrl || undefined}
                                                    sx={{ width: 60, height: 60 }}
                                                >
                                                    {item.product?.title?.[0] || '?'}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {item.product?.title || t('cart.unknownProduct')}
                                                    </Typography>
                                                    {item.price && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.price.priceType === 'subscription'
                                                                ? `Abo - ${item.price.formattedPrice}`
                                                                : item.price.priceType === 'free'
                                                                    ? 'Kostenlos'
                                                                    : 'Einmalzahlung'
                                                            }
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            {cartService.formatAmount(item.unitPrice, totals?.currency)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.quantity}
                                        </TableCell>
                                        <TableCell align="right">
                                            {cartService.formatAmount(item.lineTotal, totals?.currency)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveItem(item.id)}
                                                aria-label={t('cart.actions.remove')}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleContinueShopping}
                        >
                            {t('cart.actions.continueShopping')}
                        </Button>
                        <Button
                            color="error"
                            startIcon={<DeleteSweepIcon />}
                            onClick={handleClearCart}
                        >
                            {t('cart.actions.clear')}
                        </Button>
                    </Box>
                </Grid>

                {/* Order Summary */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Zusammenfassung
                            </Typography>

                            <Box sx={{ my: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('cart.subtotal')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {totals ? cartService.formatAmount(totals.subtotal, totals.currency) : '-'}
                                    </Typography>
                                </Box>

                                {totals && totals.discount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('cart.discount')}
                                        </Typography>
                                        <Typography variant="body2" color="success.main">
                                            -{cartService.formatAmount(totals.discount, totals.currency)}
                                        </Typography>
                                    </Box>
                                )}

                                {totals && totals.tax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('cart.tax')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {cartService.formatAmount(totals.tax, totals.currency)}
                                        </Typography>
                                    </Box>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="h6">
                                        {t('cart.total')}
                                    </Typography>
                                    <Typography variant="h6" color="primary">
                                        {totals ? cartService.formatAmount(totals.total, totals.currency) : '-'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleCheckout}
                                sx={{ mt: 2 }}
                            >
                                {t('cart.actions.checkout')}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CartPage;
